// @flow

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { intersection } from 'lodash-es'

import { changeDirectory, renameFile } from '../../core/api/api.file'
import Checkbox from '../Checkbox/Checkbox'
import { FOLDER_TYPE } from './Files.constants'
import ModalHead from '../Modal/ModalHead'
import ModalCloseButton from '../Modal/ModalCloseButton'
import { useOverflow, useUser } from '../../hooks'
import { isManagerUser } from '../../utils/utils'
import NewSelectSimple from '../NewSelectSimple'
import { USER_GROUPS } from '../../constants/users'

const ChangeTypePopup = props => {
  const { title, buttonTitle, name, permissionGroups, id, type } = props

  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState(permissionGroups.split(', '))
  const [isInherit, setInherit] = useState(true)

  const { t } = useTranslation('Files')

  useOverflow()

  const user = useUser()

  const isManager = isManagerUser(user)

  const accessOptions = Object.keys(USER_GROUPS)
    .slice(0, 4)
    .map(group => ({
      label: t(`Request:Role_${group}`),
      value: group,
    }))

  const getOptionTitle = option => {
    if (option.value === USER_GROUPS.admin) {
      return t('CannotRemoveAdminAccessWarning')
    }

    if (option.value === USER_GROUPS.manager && isManager) {
      return t('CannotRemoveOwnerAccessWarning')
    }

    if (
      option.value === USER_GROUPS.manager &&
      intersection(value, [USER_GROUPS.dweller, USER_GROUPS.landlord]).length >
        0
    ) {
      return t('CannotRemoveManagerAccessWarning')
    }

    return null
  }

  const handleChangeAccess = option => {
    if (getOptionTitle(option)) {
      return
    }

    if (value.includes(option.value)) {
      setValue(value.filter(v => v !== option.value))
    } else {
      if (
        [USER_GROUPS.dweller, USER_GROUPS.landlord].includes(option.value) &&
        !value.includes(USER_GROUPS.manager)
      ) {
        setValue(value.concat(option.value).concat(USER_GROUPS.manager))
      } else {
        setValue(value.concat(option.value))
      }
    }
  }

  const handleSave = () => {
    setLoading(true)

    const params = Object.assign(
      {},
      { set_permission_groups: value },
      FOLDER_TYPE === type
        ? { inherit_permission_groups: isInherit }
        : undefined
    )

    let api = renameFile

    if (FOLDER_TYPE === type) {
      api = changeDirectory
    }

    api(id, params)
      .then(() => {
        setLoading(false)
        props.setLoading(true)
        props.closePopup()
      })
      .catch(error => props.setError(error))
  }

  const handleInherit = () => setInherit(isInherit => !isInherit)

  return (
    <>
      <ModalHead title={t(title)} />
      <ModalCloseButton onClose={props.closePopup} />
      <div className='modal__body'>
        <div className='m-a10__row'>
          <div className='m-a10__cell m-a10__cell_1'>
            <span className='m-a10__label'>{t('FolderName')}</span>
          </div>
          <div className='m-a10__cell m-a10__cell_2'>
            <div className='input input--medium input--block input--default'>
              <input
                disabled
                type='text'
                name='name'
                placeholder={t('FolderNamePlaceholder')}
                value={name}
              />
            </div>
          </div>
        </div>
        <div className='m-a10__row'>
          <div className='m-a10__cell m-a10__cell_1'>
            <span className='m-a10__label'>{t('FolderAccess')}</span>
          </div>
          <div className='m-a10__cell m-a10__cell_2'>
            <div className='input input--medium input--block input--default'>
              <NewSelectSimple
                isMulti
                name='users-access'
                getSelectedLabel={option =>
                  accessOptions.find(o => o.value === option)?.label
                }
                getListTitle={option => getOptionTitle(option)}
                selectedItems={value}
                setSelectedItems={handleChangeAccess}
                options={accessOptions}
                placeholder={t('FolderAccess')}
                onChange={handleChangeAccess}
              />
            </div>
          </div>
        </div>
        {type === FOLDER_TYPE && (
          <div className='m-a10__row'>
            <div className='m-a10__cell m-a10__cell_1'>
              <span className='m-a10__label'>{t('Inherit')}</span>
            </div>
            <div className='input input--medium input--block input--default'>
              <Checkbox checked={isInherit} onChange={handleInherit} />
            </div>
          </div>
        )}
      </div>
      <div className='modal__submit'>
        <button
          className='button button--large button--success'
          type='button'
          disabled={loading}
          onClick={handleSave}
        >
          {loading && <span className='button__spinner' />}
          {t(buttonTitle)}
        </button>
        <button
          disabled={loading}
          className='button button--large button--danger-3'
          type='button'
          onClick={props.closePopup}
        >
          {t('Cancel')}
        </button>
      </div>
    </>
  )
}

export default ChangeTypePopup
